import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../api/api-context.js';
import { isApiRequestError } from '../api/errors.js';
import { fetchMe, signOut as signOutRequest } from './auth-api.js';
import {
  ANONYMOUS,
  AuthContext,
  type AuthApi,
  type AuthSnapshot,
  type AuthState,
} from './auth-context.js';
import { onSessionExpired } from './session-expiry.js';

/**
 * The session, held in one place for the whole app.
 *
 * On boot it asks the server rather than trusting anything in the browser, because the tokens
 * are httpOnly cookies this code cannot read — which is the point of them. That one call is
 * also what performs the silent refresh: the API client turns a 401 into a refresh-and-replay,
 * so a laptop reopened after lunch resumes its session instead of being shown a login form.
 *
 * A NETWORK failure on boot is not "signed out". A shop on a dropping 4G connection must not
 * be logged out by a lost packet, so only an actual 401 or 403 clears the session; anything
 * else leaves the app anonymous but says so as a connection problem, and reloading recovers.
 */
export interface AuthProviderProps {
  children: ReactNode;
  /**
   * Skip the boot request and start from this session. Tests use it to mount a private screen
   * as a signed-in owner; nothing in the application passes it.
   */
  initial?: AuthSnapshot | null;
}

function snapshotToState(snapshot: AuthSnapshot): AuthState {
  return {
    status: 'authenticated',
    user: snapshot.user,
    shop: snapshot.shop,
    role: snapshot.role,
    sessionId: snapshot.sessionId,
    memberships: snapshot.memberships,
    expired: false,
  };
}

export function AuthProvider({ children, initial }: AuthProviderProps): ReactElement {
  const client = useApiClient();
  const queryClient = useQueryClient();

  const [state, setState] = useState<AuthState>(() =>
    initial ? snapshotToState(initial) : { ...ANONYMOUS, status: 'loading' },
  );

  // `initial` is a test seam, so it is read once and never watched: a re-render must not be
  // able to silently re-authenticate somebody who has just signed out.
  const bootstrapped = useRef(initial !== undefined && initial !== null);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;

    void (async () => {
      try {
        const me = await fetchMe(client);
        applyBoot(
          snapshotToState({
            user: me.user,
            shop: me.shop,
            role: me.role,
            sessionId: me.sessionId,
            memberships: me.memberships,
          }),
        );
      } catch (error) {
        // 401/403 is a real "not signed in". Anything else — a dropped connection, a 500 —
        // leaves us anonymous for now, and the login screen's own error handling takes over
        // rather than this boot path inventing a message.
        const refused = isApiRequestError(error) && (error.status === 401 || error.status === 403);
        if (!refused) {
          // Not a credential problem: worth a line in the console for whoever is debugging.
          console.warn('[auth] could not read the current session', error);
        }
        applyBoot({ ...ANONYMOUS });
      }
    })();

    /**
     * The answer is applied only while the session is still unknown, and NOT discarded on
     * cleanup.
     *
     * Under StrictMode React mounts every effect twice — run, clean up, run — and the second
     * run is stopped by `bootstrapped`. A cleanup that abandoned the in-flight request would
     * therefore throw away the only boot request the app ever makes and leave it on its
     * loading splash forever, which is exactly what `npm run dev` used to show.
     *
     * Checking for `loading` is what the cancellation was actually protecting: somebody who
     * has signed in or out while the boot request was in the air keeps that newer answer.
     */
    function applyBoot(next: AuthState) {
      setState((previous) => (previous.status === 'loading' ? next : previous));
    }
  }, [client]);

  /** A 401 the client could not refresh away. Keep the screen; ask for the password over it. */
  useEffect(
    () =>
      onSessionExpired(() => {
        setState((previous) =>
          previous.status === 'authenticated' ? { ...previous, expired: true } : previous,
        );
      }),
    [],
  );

  const signIn = useCallback(
    (snapshot: AuthSnapshot) => {
      setState(snapshotToState(snapshot));
      // Everything cached was read as somebody else, or before the session died. Drop it.
      void queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      await signOutRequest(client);
    } catch {
      // The server may already have forgotten this session, or the network may be down.
      // Either way the local session ends: refusing to sign out because the request failed
      // leaves the shop's phone signed in on a counter nobody is standing at.
    }
    setState({ ...ANONYMOUS });
    queryClient.clear();
  }, [client, queryClient]);

  /**
   * Re-read the session after something that may have moved it — a plan change, a cancellation.
   *
   * A failure here is absorbed, following the same rule as the boot path above. Every caller
   * fires this and carries on, so a rejection would arrive as an unhandled rejection rather than
   * as anything a person could act on; and the credential case is already covered elsewhere,
   * because the API client refreshes a 401 and puts the app on the expiry path when it cannot.
   * Anything else — a dropped connection, a 500 — keeps the session already on screen, which is
   * the honest answer: nothing was learned, so nothing changes.
   */
  const reload = useCallback(async () => {
    try {
      const me = await fetchMe(client);
      setState(
        snapshotToState({
          user: me.user,
          shop: me.shop,
          role: me.role,
          sessionId: me.sessionId,
          memberships: me.memberships,
        }),
      );
    } catch (error) {
      const refused = isApiRequestError(error) && (error.status === 401 || error.status === 403);
      if (!refused) console.warn('[auth] could not refresh the current session', error);
    }
  }, [client]);

  const value = useMemo<AuthApi>(
    () => ({ ...state, signIn, signOut, reload }),
    [state, signIn, signOut, reload],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
